<script setup>
/* global axios */
// Migrador de caixa: move os tickets/dados de uma caixa (origem) p/ outra (destino)
// do MESMO tipo. Uso: aluno recria uma caixa e não quer perder os tickets.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'migrated']);
const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;

// tipos em que dá pra CRIAR a caixa destino clonando a origem (espelha o backend)
const CLONABLE_TYPES = ['Channel::Api', 'Channel::WebWidget'];

const inboxes = useMapGetter('inboxes/getInboxes');
const sourceId = ref('');
const targetId = ref('');
const mode = ref('existing'); // 'existing' | 'new'
const newName = ref('');
const migrating = ref(false);
const result = ref(null);
const confirmText = ref('');

const inboxById = id => inboxes.value.find(i => i.id === Number(id));
// mesmo tipo = mesmo channel_type; no WhatsApp exige o mesmo provider (oficial vs não)
const sameType = (a, bId) => {
  const b = inboxById(bId);
  if (!a || !b || a.id === b.id) return false;
  if (a.channel_type !== b.channel_type) return false;
  if (a.channel_type === 'Channel::Whatsapp') return a.provider === b.provider;
  return true;
};
const source = computed(() => inboxById(sourceId.value));
const sourceClonable = computed(
  () => source.value && CLONABLE_TYPES.includes(source.value.channel_type)
);
const targetOptions = computed(() =>
  source.value ? inboxes.value.filter(i => sameType(source.value, i.id)) : []
);
const confirmed = computed(
  () =>
    confirmText.value.trim().toUpperCase() ===
    t('INBOX_MGMT.MIGRATE_CONFIRM_WORD').toUpperCase()
);
const canMigrate = computed(() => {
  if (!source.value || !confirmed.value) return false;
  if (mode.value === 'new') return sourceClonable.value && newName.value.trim();
  return targetId.value && sameType(source.value, targetId.value);
});

const typeLabel = i => {
  if (!i) return '';
  if (i.channel_type === 'Channel::Whatsapp')
    return i.provider === 'whatsapp_cloud'
      ? 'WhatsApp API Oficial'
      : 'WhatsApp';
  return (i.channel_type || '').replace('Channel::', '');
};

const migrate = async () => {
  if (!canMigrate.value) return;
  migrating.value = true;
  result.value = null;
  try {
    const payload =
      mode.value === 'new'
        ? { source_id: sourceId.value, new_inbox_name: newName.value.trim() }
        : { source_id: sourceId.value, target_id: targetId.value };
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId}/kanban/inbox_migrations`,
      payload
    );
    result.value = data.data;
    useAlert(t('INBOX_MGMT.MIGRATE.SUCCESS'));
    emit('migrated');
  } catch (e) {
    useAlert(e?.response?.data?.error || t('INBOX_MGMT.MIGRATE.ERROR'));
  } finally {
    migrating.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
    @click.self="emit('close')"
  >
    <div
      class="w-[34rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
      >
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('INBOX_MGMT.MIGRATE.TITLE') }}
        </h2>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <div class="p-6 space-y-4">
        <p class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.MIGRATE.HELP') }}
        </p>

        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('INBOX_MGMT.MIGRATE.FROM')
          }}</label>
          <select
            v-model="sourceId"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="
              targetId = '';
              newName = '';
              if (!sourceClonable) mode = 'existing';
            "
          >
            <option value="">{{ t('INBOX_MGMT.MIGRATE.SELECT') }}</option>
            <option v-for="i in inboxes" :key="i.id" :value="i.id">
              {{ i.name }} — {{ typeLabel(i) }}
            </option>
          </select>
        </div>

        <!-- destino: caixa existente ou criar nova (só p/ tipos clonáveis) -->
        <div v-if="source" class="flex gap-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm rounded-lg border"
            :class="
              mode === 'existing'
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="mode = 'existing'"
          >
            {{ t('INBOX_MGMT.MIGRATE.MODE_EXISTING') }}
          </button>
          <button
            v-if="sourceClonable"
            type="button"
            class="flex-1 px-3 py-2 text-sm rounded-lg border"
            :class="
              mode === 'new'
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="mode = 'new'"
          >
            {{ t('INBOX_MGMT.MIGRATE.MODE_NEW') }}
          </button>
        </div>

        <div v-if="mode === 'existing'">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('INBOX_MGMT.MIGRATE.TO')
          }}</label>
          <select
            v-model="targetId"
            :disabled="!source"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 disabled:opacity-50"
          >
            <option value="">{{ t('INBOX_MGMT.MIGRATE.SELECT') }}</option>
            <option v-for="i in targetOptions" :key="i.id" :value="i.id">
              {{ i.name }} — {{ typeLabel(i) }}
            </option>
          </select>
          <p
            v-if="source && !targetOptions.length"
            class="mt-1 text-xs text-n-amber-11"
          >
            {{ t('INBOX_MGMT.MIGRATE.NO_TARGET') }}
          </p>
        </div>

        <div v-else>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('INBOX_MGMT.MIGRATE.NEW_NAME')
          }}</label>
          <input
            v-model="newName"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('INBOX_MGMT.MIGRATE.NEW_NAME_PLACEHOLDER')"
          />
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.MIGRATE.NEW_HELP') }}
          </p>
        </div>

        <div
          class="p-3 text-xs rounded-lg bg-n-amber-2 border border-n-amber-6 text-n-amber-11"
        >
          {{ t('INBOX_MGMT.MIGRATE.WARNING') }}
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('INBOX_MGMT.MIGRATE.CONFIRM_LABEL')
          }}</label>
          <input
            v-model="confirmText"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('INBOX_MGMT.MIGRATE_CONFIRM_WORD')"
          />
        </div>

        <div
          v-if="result"
          class="p-3 text-sm rounded-lg bg-n-teal-2 border border-n-teal-6 text-n-teal-11"
        >
          {{
            t('INBOX_MGMT.MIGRATE.DONE', {
              conversations: result.conversations,
              contacts: result.contacts,
              messages: result.messages,
            })
          }}
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak"
      >
        <Button
          :label="t('INBOX_MGMT.MIGRATE.CANCEL')"
          variant="faded"
          size="sm"
          @click="emit('close')"
        />
        <Button
          :label="
            migrating
              ? t('INBOX_MGMT.MIGRATE.MIGRATING')
              : t('INBOX_MGMT.MIGRATE.ACTION')
          "
          color="ruby"
          size="sm"
          :disabled="!canMigrate || migrating"
          @click="migrate"
        />
      </div>
    </div>
  </div>
</template>
